import React, { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TVEventHandler, View } from 'react-native';
import PropTypes from 'prop-types';

import { Theme } from '@constants';

import { VideoControls } from './Controls';

import { VOD_Icons } from '../../../assets/icons/vod/index';
import { isWeb } from '../../../helpers/Platform';
import { TWCButton } from '../../focusable/TWCButton/TWCButton';
import { TWCText } from '../../primitives/TWCText/TWCText';

export const PlayPauseButton = forwardRef(
  (
    {
      testID,
      nextFocusUp,
      nextFocusLeft,
      nextFocusRight,
      nextFocusDown,
      showControls,
      onControl,
      accessibilityLabel,
      accessibilityLabelActive,
      accessibilityHint,
      accessibilityHintActive,
      paused = false,
      onFocus,
    },
    reference,
  ) => {
    const tPath = 'videoControls';
    const { t } = useTranslation('translation', { keyPrefix: tPath });

    const [focused, setFocused] = useState(false);

    const controlHandler = keyEvent => {
      const key = keyEvent.key;

      if (key === 'playPause') {
        showControls && showControls();
        onControl && onControl(VideoControls.TOGGLE_PAUSE);
      }
    };

    useEffect(() => {
      const tvEventHandler = new TVEventHandler();

      if (isWeb) {
        document.addEventListener('keydown', controlHandler, true);
      } else {
        const handleEvent = (cmp, event) => {
          if (event && event.eventType && event.eventType === 'playPause' && event.eventKeyAction === 0) {
            controlHandler({ key: event.eventType });
          }
        };

        tvEventHandler.enable(null, handleEvent);
      }
      return () => {
        if (isWeb) {
          document.removeEventListener('keydown', controlHandler, true);
        } else {
          tvEventHandler?.disable();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFocusHandler = () => {
      showControls?.();
      setFocused(true);
      onFocus?.();
    };

    const onPressHandler = () => {
      showControls?.();
      onControl?.(VideoControls.TOGGLE_PAUSE);
    };

    return (
      <View style={styles.ppContainer}>
        <TWCButton
          accessibilityHint={paused ? accessibilityHintActive : accessibilityHint}
          accessibilityLabel={paused ? accessibilityLabelActive : accessibilityLabel}
          height={60}
          icon={paused ? VOD_Icons.play : VOD_Icons.pause}
          iconFocused={paused ? VOD_Icons.play : VOD_Icons.pause}
          nativeID={testID}
          nextFocusDown={nextFocusDown}
          nextFocusLeft={nextFocusLeft}
          nextFocusRight={nextFocusRight}
          nextFocusUp={nextFocusUp}
          ref={reference}
          shouldUseContextReference={false}
          styleFocused={styles.buttonFocus}
          testID={testID}
          textFocusStyle={styles.buttonTextFocus}
          textStyle={styles.ccButtonTextFocus}
          width={84}
          onBlur={() => setFocused(false)}
          onFocus={onFocusHandler}
          onPress={onPressHandler}
        />
        {focused && (
          <TWCText
            importantForAccessibility={'no'}
            style={styles.ccLabelText}
            testID={`${testID}_label`}
            variant={'mediumText18'}
          >
            {paused ? t('playHint') : t('pauseHint')}
          </TWCText>
        )}
      </View>
    );
  },
);

PlayPauseButton.propTypes = {
  testID: PropTypes.string,
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  showControls: PropTypes.func,
  onControl: PropTypes.func,
  accessibilityLabel: PropTypes.string,
  accessibilityHint: PropTypes.string,
  accessibilityHintEnabled: PropTypes.string,
  paused: PropTypes.bool,
  onFocus: PropTypes.func,
};

PlayPauseButton.defaultProps = {
  testID: null,
  nextFocusUp: null,
  nextFocusLeft: null,
  nextFocusRight: null,
  nextFocusDown: null,
  showControls: () => {},
  onControl: () => {},
  accessibilityLabel: null,
  accessibilityHint: null,
  accessibilityHintEnabled: null,
  paused: false,
  onFocus: () => {},
};

PlayPauseButton.displayName = 'PlayPauseButton';

const styles = StyleSheet.create({
  buttonFocus: {
    backgroundColor: Theme.dodgerLightBlue,
  },
  buttonTextFocus: {
    color: Theme.mainText,
    fontFamily: 'Roboto-Bold',
    fontSize: Theme.px(30),
  },
  ccLabelText: {
    marginTop: Theme.px(4),
  },
  ppContainer: {
    alignItems: 'center',
    flexDirection: 'column',
    marginRight: Theme.px(30),
  },
});
